/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2025, Zumper
 */

import type { ApiError as ApiErrorType, RawApiError } from './apiError.types'
import { Bizo, autoclass } from './base'
import { SerializableMap } from './serializableMap'

export const ApiError = Object.assign(
  autoclass<ApiErrorType, RawApiError>(
    'ApiError',
    class ApiError extends Bizo<ApiErrorType, RawApiError> {},
    {
      failures: ({ _data: { failures } }: ApiErrorType) => {
        if (typeof failures === 'object' && !Array.isArray(failures) && failures) {
          let map: SerializableMap<string, string>
          if (
            SerializableMap.isRawSerializableMapOfType(
              failures,
              (key) => typeof key === 'string',
              (value) => typeof value === 'string'
            )
          ) {
            map = new SerializableMap<string, string>(failures)
          } else {
            map = new SerializableMap<string, string>(
              Object.entries(failures).filter(
                ([key, value]) => typeof key === 'string' && typeof value === 'string'
              )
            )
          }
          if (map.size) {
            return map
          }
        }
      },
      field: 'field',
      reason: 'reason',
      status: 'status',
    }
  ),
  {
    isRawApiError(
      error: unknown
    ): error is { readonly reason: string; readonly field?: string; readonly status: number } {
      return (
        typeof error === 'object' &&
        error != null &&
        'reason' in error &&
        'status' in error &&
        typeof error.reason === 'string' &&
        !!error.reason.trim() &&
        typeof error.status === 'number' &&
        (!('field' in error) || typeof error.field === 'string')
      )
    },
  } satisfies Pick<typeof ApiErrorType, 'isRawApiError'>
)

export type ApiError = ApiErrorType
